import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Instagram, Linkedin } from 'lucide-react';

const LEGAL_LINKS = [
  { label: 'Privacy Policy', path: '/privacy-policy' },
  { label: 'Terms of Service', path: '/terms-of-service' },
  { label: 'Contact Us', path: '/contact-us' },
  { label: 'FAQ', path: '/faq' },
] as const;

const SOCIALS = [
  { label: 'Instagram', href: 'https://instagram.com/lifeatdressup', Icon: Instagram },
  { label: 'LinkedIn', href: 'https://www.linkedin.com/company/thedressup/', Icon: Linkedin },
] as const;

const Footer: React.FC = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-panel text-panel-ink">
      <div className="shell py-10 md:py-20">
        <div className="grid grid-cols-2 gap-x-6 gap-y-8 md:grid-cols-12 md:gap-12">
          <div className="col-span-2 md:col-span-6">
            <p className="display text-[clamp(2.25rem,9vw,5rem)] font-medium leading-none">
              Dress<span className="text-panel-muted">Up</span>
            </p>
            <p className="mt-4 max-w-[38ch] text-[14px] leading-relaxed text-panel-muted md:mt-6 md:text-[15px]">
              A marketplace that stocks itself around your coloring, your size and the brands you
              actually wear.
            </p>
          </div>

          <nav className="md:col-span-3" aria-label="Legal">
            <p className="text-[13px] font-semibold text-panel-ink">Legal</p>
            <ul className="mt-2 sm:mt-4">
              {LEGAL_LINKS.map((link) => (
                <li key={link.path}>
                  <button
                    type="button"
                    onClick={() => navigate(link.path)}
                    className="inline-flex min-h-[40px] items-center text-[14px] text-panel-muted transition-colors hover:text-panel-ink"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          <div className="md:col-span-3">
            <p className="text-[13px] font-semibold text-panel-ink">Elsewhere</p>
            <ul className="mt-2 sm:mt-4">
              <li>
                <a
                  href="https://apps.shopify.com/dressup"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex min-h-[40px] items-center text-[14px] text-panel-muted transition-colors hover:text-panel-ink"
                >
                  Shopify app
                </a>
              </li>
              <li>
                <a
                  href="https://app.thedressup.co/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex min-h-[40px] items-center text-[14px] text-panel-muted transition-colors hover:text-panel-ink"
                >
                  Open the web app
                </a>
              </li>
            </ul>

            <div className="mt-4 flex gap-3 md:mt-6">
              {SOCIALS.map(({ label, href, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="flex h-11 w-11 items-center justify-center border border-panel-line text-panel-muted transition-colors hover:border-panel-ink hover:text-panel-ink"
                >
                  <Icon strokeWidth={1.5} className="h-5 w-5" />
                </a>
              ))}
            </div>
          </div>
        </div>

        <p className="mt-8 border-t border-panel-line pt-5 text-[13px] text-panel-muted md:mt-14 md:pt-6">
          &copy; {currentYear} DressUp. All rights reserved.
        </p>
      </div>
    </footer>
  );
};

export default Footer;
